import useComputed from '@/hooks/use-computed'
import api from '@/services/api/dataSchemaVersioning'
import { useState } from 'react'
import { formatSchemaBusinessTerm, resolveFieldDataType } from '@/pages/data-schema-versioning/dsv-schema-utils'
import {
    isLeafDescriptionBlob,
    parseLeafDescription,
} from '@/pages/data-schema-versioning/leaf-description'

/**
 * Serialize a DataElementVersion from the BE response to a list-page friendly format.
 * BE model fields:
 *   id, dataElementId, version, changeType, changeDescription, status,
 *   expiry, elementName, elementSchema, querySchema, jsonTemplate, csvTemplate,
 *   type (DOCUMENT|EVENT|ETR), publishedDate, retiredDate, createdBy, modifiedBy, createdAt, updatedAt
 */
export function serializeListItem(entry) {
    return {
        id: entry.id,
        dataElementId: entry.dataElementId,
        elementName: entry.elementName,
        version: entry.version,
        changeType: entry.changeType,       // INITIAL | PATCH | MINOR | MAJOR
        changeDescription: entry.changeDescription,
        status: entry.status,               // DRAFT | ACTIVE | DEPRECATED | RETIRED
        type: entry.type,                   // DOCUMENT | EVENT | ETR
        publishedDate: entry.publishedDate,
        retiredDate: entry.retiredDate,
        expiry: entry.expiry,
        createdAt: entry.createdAt,
        updatedAt: entry.updatedAt,
        elementSchemaJson: entry.elementSchemaJson || null,
    }
}

/**
 * Serialize a DataElementVersion to detail/version-history format
 */
export function serializeVersionDetail(entry) {
    return {
        id: entry.id,
        dataElementId: entry.dataElementId,
        elementName: entry.elementName,
        version: entry.version,
        changeType: entry.changeType,
        changeDescription: entry.changeDescription,
        status: entry.status,
        type: entry.type,
        publishedDate: entry.publishedDate,
        retiredDate: entry.retiredDate,
        expiry: entry.expiry,
        elementSchema: typeof entry.elementSchema === 'string'
            ? JSON.parse(entry.elementSchema)
            : entry.elementSchema,
        elementSchemaJson: entry.elementSchemaJson || null,
        querySchema: entry.querySchema,
        jsonTemplate: entry.jsonTemplate,
        csvTemplate: entry.csvTemplate,
        createdBy: entry.createdBy,
        modifiedBy: entry.modifiedBy,
        createdByEmail: entry.createdByEmail || null,
        modifiedByEmail: entry.modifiedByEmail || null,
        createdAt: entry.createdAt,
        updatedAt: entry.updatedAt,
    }
}

/**
 * Count how many leaf `description` values across a schema's top-level
 * `properties` are the canonical `<root>...</root>` XML blob. Used to decide
 * whether a schema variant still carries the structured description metadata
 * or has been @AfterLoad-stripped.
 */
function countLeafDescriptionBlobs(schema) {
    if (!schema || typeof schema !== 'object' || !schema.properties) return 0;
    let n = 0;
    for (const value of Object.values(schema.properties)) {
        if (value && isLeafDescriptionBlob(value.description)) n += 1;
    }
    return n;
}

/**
 * Choose which stored schema variant to hydrate the editor from.
 *
 * `elementSchema` is the authoritative, validated schema the autosave PATCH
 * writes to; after any edit it reflects the user's latest types/constraints
 * AND carries the full `<root>` description blobs the editor emits.
 * `elementSchemaJson` is a clone kept only to survive the backend's @AfterLoad
 * stripping of description sub-labels from `elementSchema` on load — but it is
 * NOT updated by autosave PATCHes, so it goes stale the moment a field is
 * edited (the original cause of edits reverting on reload).
 *
 * Rule: prefer `elementSchema` (freshest). Only fall back to
 * `elementSchemaJson` when `elementSchema` looks description-stripped while the
 * clone still carries strictly more description blobs — the exact scenario the
 * clone exists for. This keeps description fidelity without ever resurrecting a
 * stale pre-edit schema.
 */
export function pickHydrationSchema(elementSchema, elementSchemaJson) {
    if (!elementSchemaJson) return elementSchema || null;
    if (!elementSchema) return elementSchemaJson;
    const liveBlobs = countLeafDescriptionBlobs(elementSchema);
    const cloneBlobs = countLeafDescriptionBlobs(elementSchemaJson);
    // elementSchema was stripped of descriptions the clone still has — use clone.
    if (cloneBlobs > liveBlobs) return elementSchemaJson;
    return elementSchema;
}

/**
 * Extract schema fields from elementSchema JSON Schema format.
 * The BE stores fields as JSON Schema: { properties: { fieldName: { title, type, description, ... } }, required: [...] }
 */
export function extractSchemaFields(elementSchema, parentKey) {
    if (!elementSchema || !elementSchema.properties) return [];

    const requiredFields = elementSchema.required || [];
    const fields = [];

    for (const [key, value] of Object.entries(elementSchema.properties)) {
        const fieldId = parentKey ? `${parentKey}.${key}` : key;
        const type = resolveFieldDataType(value);
        // Track `type: ["string", "null"]` (etc.) as a separate flag so the
        // emitter can re-wrap on round-trip. Without this, nullable fields
        // collapse to a primitive and the rebuilt schema rejects nulls the
        // source accepts.
        const nullable = Array.isArray(value?.type) && value.type.includes('null');

        // example (singular, draft-7) vs examples (plural, 2020-12) both
        // appear in production schemas. Capture which keyword the source
        // authored so we round-trip the same form.
        let exampleValue = '';
        let exampleKeyword = null;
        if (Object.prototype.hasOwnProperty.call(value, 'example')) {
            exampleValue = value.example ?? '';
            exampleKeyword = 'example';
        } else if (Array.isArray(value?.examples) && value.examples.length > 0) {
            exampleValue = value.examples[0] ?? '';
            exampleKeyword = 'examples';
        }

        // Real production schemas store every leaf description as a small
        // `<root><div><label>X</label>:<b>val</b></div>...</root>` blob with
        // five named sub-fields. Parse it here so each sub-field is editable
        // in the field modal; the emitter re-serializes on save. When the
        // description is plain text (or absent), the structured fields stay
        // empty and the original string flows through unchanged.
        //
        // We also stash the original raw blob (`originalDescriptionBlob`) so
        // the emitter can short-circuit and re-emit byte-identically when
        // the user hasn't edited any of the structured fields. Without this,
        // production schemas whose blob `businessTerm` disagrees with the
        // schema's `title` (a real inconsistency in admin_buildex_dev) would
        // see one of those values silently rewritten on round-trip.
        const parsedDesc = isLeafDescriptionBlob(value.description)
            ? parseLeafDescription(value.description)
            : null;
        const description = parsedDesc ? parsedDesc.description : (value.description || '');
        const businessDefinition = parsedDesc ? parsedDesc.businessDefinition : '';
        const applicableStandardName = parsedDesc ? parsedDesc.applicableStandardName : '';
        const dataFormat = parsedDesc ? parsedDesc.dataFormat : '';
        const originalDescriptionBlob = parsedDesc ? value.description : null;

        const field = {
            fieldId,
            dataElementName: key,
            businessTerm: formatSchemaBusinessTerm(key, value, parentKey),
            mandatory: requiredFields.includes(key),
            dataType: type,
            description,
            businessDefinition,
            applicableStandardName,
            dataFormat,
            // Mirror uniqueItemsExplicit's pattern: track whether the
            // uploaded schema authored a `<root>` blob vs plain text so the
            // emitter knows whether to re-serialize the structured form on
            // round-trip. Without this, plain-text descriptions would be
            // silently upgraded to structured form on first save.
            descriptionIsStructured: parsedDesc !== null,
            originalDescriptionBlob,
            // Mirror uniqueItemsExplicit (GAP-5): track whether the source
            // schema authored a `description` keyword at all, so the emitter
            // can preserve an explicit `description: ""` (e.g. on array
            // wrapper fields) instead of deleting it on round-trip.
            descriptionExplicit: Object.prototype.hasOwnProperty.call(value, 'description'),
            // x-uitype / x-uioptions are non-JSON-Schema extensions used by
            // the renderer (datepicker / dropdown / file-upload etc). Their
            // shape is polymorphic (dropdown stores a JSON-encoded string,
            // radio/multi-select store arrays, file-upload/dynamic-select
            // store objects). Capture them as opaque values so byte-for-byte
            // round-trip survives even when the editor doesn't yet know how
            // to author each shape.
            xUitype: typeof value['x-uitype'] === 'string' ? value['x-uitype'] : null,
            xUioptions: Object.prototype.hasOwnProperty.call(value, 'x-uioptions')
                ? value['x-uioptions']
                : undefined,
            format: value.format || null,
            exampleValue,
            // Mirror uniqueItemsExplicit / descriptionExplicit: track which
            // example keyword the source authored ('example' | 'examples')
            // so the emitter doesn't normalize singular → plural on save.
            exampleKeyword,
            // True when the source `type` was an array including 'null'.
            // The emitter re-wraps the resolved primitive as `[type, 'null']`
            // when this is set. Surfaced in the field editor as the "Accept
            // null" checkbox, and in the confirm diff as its own row.
            //
            // Deliberately SOURCE-FAITHFUL: an optional-but-not-nullable
            // property hydrates as `nullable: false` even though the save will
            // widen it (normalizeOptionalNullable, DSV-017). This value is the
            // diff's only "before" — normalizing here would erase the evidence
            // that the stored schema was broken and make the self-heal
            // undiffable.
            nullable,
            status: null,
            parentKey: parentKey || null,
            children: [],
            arrayItemType: null,
            // Validation constraints
            minLength: value.minLength ?? null,
            maxLength: value.maxLength ?? null,
            minimum: value.minimum ?? null,
            maximum: value.maximum ?? null,
            exclusiveMinimum: value.exclusiveMinimum ?? null,
            exclusiveMaximum: value.exclusiveMaximum ?? null,
            pattern: value.pattern ?? null,
            enumValues: value.enum ?? null,
            multipleOf: value.multipleOf ?? null,
            minItems: value.minItems ?? null,
            maxItems: value.maxItems ?? null,
            uniqueItems: value.uniqueItems ?? null,
            // const: a fixed-value constraint authored by JSON Schema. The
            // editor has no UI surface for `const`, so capture it as an
            // opaque value and re-emit on save. Without this, every primitive
            // const declaration silently disappears on round-trip.
            constValue: Object.prototype.hasOwnProperty.call(value, 'const') ? value.const : undefined,
            constExplicit: Object.prototype.hasOwnProperty.call(value, 'const'),
            // Distinguish "the uploaded schema authored uniqueItems" from
            // "uniqueItems was absent" so a `false` value survives the
            // round-trip (GAP-5). Without this, the emitter can't tell
            // `false` apart from the JSON Schema default.
            uniqueItemsExplicit: Object.prototype.hasOwnProperty.call(value, 'uniqueItems'),
            // Mirror descriptionExplicit / uniqueItemsExplicit: track whether
            // the source authored a `title` keyword. Without this gate,
            // formatSchemaBusinessTerm's hardcoded `attachments → "Attachments"`
            // injects a synthetic title where source had none (B6).
            titleExplicit: Object.prototype.hasOwnProperty.call(value, 'title'),
            // Track whether the source authored a `type` keyword. Typeless
            // schemas (e.g. `{ enum: [1, "1", true, null] }`) are valid JSON
            // Schema and intentionally accept any of the enum's union of
            // types. Without this gate the emitter inserts the
            // resolveSchemaFieldType fallback ('string'), narrowing the
            // rebuilt schema's accept set on round-trip.
            typeExplicit: Object.prototype.hasOwnProperty.call(value, 'type'),
            // Object property-count constraints. Captured for every field
            // (the emitter only re-emits when non-null) so they round-trip
            // even on propertyless objects (e.g. `{type:object,minProperties:2,
            // additionalProperties:true}`) that never reach the `type==='object'
            // && value.properties` recursion branch below. The builder has no
            // editor surface for these; without capture they silently drop.
            minProperties: value.minProperties ?? null,
            maxProperties: value.maxProperties ?? null,
        };

        // Capture object-level `additionalProperties` for EVERY object field,
        // including propertyless ones (e.g. `{type:object, minProperties:2,
        // additionalProperties:true}`) that skip the `value.properties`
        // recursion branch below. Without this, such objects lose their
        // additionalProperties (incl. an explicit `true`) on round-trip.
        if (type === 'object' && typeof value.additionalProperties === 'boolean') {
            field.additionalProperties = value.additionalProperties;
        }

        if (type === 'object' && value.properties) {
            field.children = extractSchemaFields(value, fieldId);
            // `additionalProperties` for objects-with-properties is captured by
            // the unconditional object-field capture above (covers propertyless
            // objects too). No need to re-capture here.
            if (Array.isArray(value.required)) {
                field.requiredOrder = value.required;
            }
            // Sub-object-level conditional rules. The XAllOfBuilder edits
            // root-level rules only; rules attached to nested objects have
            // no UI surface, so capture them verbatim and re-emit on save.
            // Without this, env_ms-style schemas silently lose their nested
            // if/then validation (e.g. `bess_used → require bess_supplier`).
            if (Array.isArray(value.allOf) && value.allOf.length > 0) {
                field.nestedAllOf = value.allOf;
            }
            if (Array.isArray(value['x-allOf']) && value['x-allOf'].length > 0) {
                field.nestedXAllOf = value['x-allOf'];
            }
        } else if (type === 'array') {
            const itemType = value.items?.type || 'object';
            field.arrayItemType = itemType;
            if (value.items?.properties) {
                field.children = extractSchemaFields(value.items, fieldId);
                // Production schemas place `additionalProperties: false` at
                // BOTH the array level AND the items level on attachments-
                // shaped fields. Capture them as separate slots so the
                // emitter can re-emit at both sites — current and create
                // modes (no baseSchema spread fallback) (B1).
                if (typeof value.additionalProperties === 'boolean') {
                    field.additionalProperties = value.additionalProperties;
                }
                if (typeof value.items?.additionalProperties === 'boolean') {
                    field.itemsAdditionalProperties = value.items.additionalProperties;
                }
                // Preserve source's required[] ordering (separate from
                // properties insertion order) for the items object (B3).
                if (Array.isArray(value.items.required)) {
                    field.requiredOrder = value.items.required;
                }
                // Items-level metadata (description, title, format, ...) is
                // lost in create mode because there's no originalProp.items
                // to spread from. Stash a verbatim copy minus the keys the
                // builder authors itself (type/properties/required/
                // additionalProperties) so we can spread it back in (B2).
                const itemsRawMeta = { ...value.items };
                delete itemsRawMeta.type;
                delete itemsRawMeta.properties;
                delete itemsRawMeta.required;
                delete itemsRawMeta.additionalProperties;
                if (Object.keys(itemsRawMeta).length > 0) {
                    field.itemsRawMeta = itemsRawMeta;
                }
            } else if (itemType === 'array' && value.items) {
                // Array-of-array: the UI has no editor surface for nested
                // arrays, so stash the inner `items` subtree verbatim. The
                // emitter re-attaches it on round-trip instead of dropping
                // to `{ type: 'array' }` and losing nested constraints.
                field.innerArraySchema = value.items;
            } else if (value.items && typeof value.items === 'object') {
                // Primitive-typed array (string/integer/number/boolean): the
                // UI has no editor surface for item-level keywords (enum,
                // minLength, pattern, format, ...), so stash them verbatim
                // minus `type` (the emitter authors that itself). Without
                // this the emitter rebuilds items as `{ type: itemType }`
                // and drops constraints — e.g. multi-select arrays whose
                // allowed values live in `items.enum`.
                const itemsRawMeta = { ...value.items };
                delete itemsRawMeta.type;
                if (Object.keys(itemsRawMeta).length > 0) {
                    field.itemsRawMeta = itemsRawMeta;
                }
            }
        }

        fields.push(field);
    }

    return fields;
}

/**
 * Extract the opaque root keywords the builder UI doesn't surface but must
 * round-trip verbatim: $id, root-level anyOf/oneOf. Returned as a plain
 * object suitable for round-trip via the emitter's `baseSchema` arg.
 *
 * Without this pass-through, an uploaded schema that declares root-level
 * conditional rules via anyOf/oneOf collapses to plain field-list semantics
 * after the next emit — the rules silently disappear from the saved schema.
 */
export function extractSchemaMeta(elementSchema) {
    if (!elementSchema || typeof elementSchema !== 'object') return {};
    const meta = {};
    if (Object.prototype.hasOwnProperty.call(elementSchema, '$id')) {
        meta.$id = elementSchema.$id;
    }
    if (Array.isArray(elementSchema.anyOf)) meta.anyOf = elementSchema.anyOf;
    if (Array.isArray(elementSchema.oneOf)) meta.oneOf = elementSchema.oneOf;
    return meta;
}

/**
 * Serialize a BE DataElementPersona row (fetched with withRelation=true) into
 * the plain link object the DSV link-management UI consumes. Falls back to the
 * raw useCaseId/personaId when the uc/persona relations were not loaded so the
 * UI always has something displayable.
 */
export function serializeDeLink(row) {
    return {
        dataElementPersonaId: row?.dataElementPersonaId ?? null,
        dataElementId: row?.dataElementId ?? null,
        useCaseId: row?.useCaseId ?? null,
        useCaseName: row?.uc?.useCaseName ?? row?.useCaseId ?? '',
        personaId: row?.personaId ?? null,
        personaName: row?.persona?.personaName ?? row?.personaId ?? '',
        deType: row?.deType ?? null,
    };
}

/**
 * Build query params for the GET /data-element-versions endpoint
 */
export function prepareFetchParams({ dataElementId, version, status, type, all, currentPage, pageSize, searchKey } = {}) {
    const params = new URLSearchParams();
    if (dataElementId) params.append('dataElementId', dataElementId);
    if (version) params.append('version', version);
    if (status && status !== 'ALL') params.append('status', status);
    if (type && type !== 'ALL') params.append('type', type);
    if (all) params.append('all', 'true');
    if (currentPage) params.append('currentPage', currentPage);
    if (pageSize) params.append('pageSize', pageSize);
    return params.toString();
}

const useDataSchemaVersioning = () => {
    const [error, setError] = useState()
    const [loading, setLoading] = useState(false)

    // List page state
    const [items, setItems] = useComputed([])
    const [allData, setAllData] = useState()
    const [activeVersionsMap, setActiveVersionsMap] = useState({})

    // Detail page state — all versions for one dataElement
    const [versionHistory, setVersionHistory] = useState([])
    const [currentVersionDetail, setCurrentVersionDetail] = useState(null)

    // Schema editing state
    const [schemaFields, setSchemaFields] = useComputed([])
    const [selectedVersion, setSelectedVersion] = useState(null)

    // Impact analysis
    const [impactAnalysis, setImpactAnalysis] = useState(null)

    // Conflict error (409)
    const [conflictError, setConflictError] = useState(null)

    // Comments
    const [comments, setComments] = useState([])

    // ============ List Page ============
    /**
     * Fetch paginated list of data element versions (latest per element by default)
     * GET /data-element-versions?currentPage=&pageSize=&status=&type=
     */
    async function fetchList(params) {
        setLoading(true)
        const [err, response] = await api.index(params);
        setLoading(false)
        if (err) {
            return setError(err);
        }
        // Response: { items: DataElementVersion[], meta: { currentPage, pageSize, pageCount, count } }
        if (response.items) {
            setItems(response.items.map(entry => serializeListItem(entry)))
            setAllData(response.meta);
        } else if (Array.isArray(response)) {
            // When no pagination params, response might be { items: [...] } without meta
            setItems(response.map(entry => serializeListItem(entry)))
        }
    }

    /**
     * Fetch all ACTIVE versions and build a lookup map: { dataElementId → serializedItem }
     */
    async function fetchActiveVersions() {
        const activeParams = prepareFetchParams({ status: 'ACTIVE', pageSize: 1000 });
        const [err, response] = await api.index(activeParams);
        if (err) {
            return {};
        }
        const activeItems = response.items || (Array.isArray(response) ? response : []);
        const map = {};
        for (const entry of activeItems) {
            map[entry.dataElementId] = serializeListItem(entry);
        }
        return map;
    }

    /**
     * Fetch paginated list AND all active versions in parallel.
     * Merges active version info onto each list item as `item.activeVersion`.
     *
     * Display rules:
     * - item.status === 'ACTIVE' → no merge (already showing active)
     * - active version exists for item.dataElementId → attach as item.activeVersion
     * - no active version → item.activeVersion = null (draft-only element)
     */
    async function fetchListWithActiveVersions(params) {
        setLoading(true)

        const [listResult, activeMap] = await Promise.all([
            api.index(params),
            fetchActiveVersions(),
        ]);

        setLoading(false)

        const [listErr, listResponse] = listResult;
        if (listErr) {
            return setError(listErr);
        }

        setActiveVersionsMap(activeMap);

        const rawItems = listResponse.items || (Array.isArray(listResponse) ? listResponse : []);
        const enrichedItems = rawItems.map(entry => {
            const serialized = serializeListItem(entry);
            const activeInfo = activeMap[serialized.dataElementId];

            if (activeInfo && activeInfo.status === 'ACTIVE' && serialized.status !== 'ACTIVE') {
                return { ...serialized, activeVersion: activeInfo };
            }
            return { ...serialized, activeVersion: null };
        });

        setItems(enrichedItems);
        if (listResponse.meta) {
            setAllData(listResponse.meta);
        }
    }

    // ============ Detail Page ============
    /**
     * Fetch all versions for a data element
     * GET /data-element-versions?dataElementId={id}&all=true
     */
    async function fetchAllVersions(dataElementId) {
        setLoading(true)
        const [err, response] = await api.getAllVersions(dataElementId);
        setLoading(false)
        if (err) {
            return setError(err);
        }
        // Response: { items: DataElementVersion[] } (no pagination when all=true typically)
        const versions = response.items || response || [];
        const serialized = versions.map(entry => serializeVersionDetail(entry));

        // Sort by version descending (latest first)
        serialized.sort((a, b) => {
            const aParts = a.version.split('.').map(Number);
            const bParts = b.version.split('.').map(Number);
            for (let i = 0; i < 3; i++) {
                if ((bParts[i] || 0) !== (aParts[i] || 0)) return (bParts[i] || 0) - (aParts[i] || 0);
            }
            return 0;
        });

        setVersionHistory(serialized);

        // Set the current (highest ACTIVE or latest) version as detail
        const activeVersion = serialized.find(v => v.status === 'ACTIVE') || serialized[0];
        setCurrentVersionDetail(activeVersion || null);

        return serialized;
    }

    /**
     * Fetch a single version by dataElementId + version number
     * GET /data-element-versions/:dataElementId/versions/:version
     */
    async function fetchSingleVersion(dataElementId, version) {
        setLoading(true)
        const [err, response] = await api.getByVersion(dataElementId, version);
        setLoading(false)
        if (err) {
            return setError(err);
        }
        const detail = serializeVersionDetail(response);
        setSelectedVersion(detail);

        // Pick the freshest complete schema. elementSchemaJson is an XML-
        // preserving clone that survives the backend @AfterLoad stripping of
        // description sub-labels, but it is NOT updated by autosave PATCHes —
        // so blindly preferring it resurrected pre-edit (stale) state on
        // reload. pickHydrationSchema prefers the authoritative elementSchema
        // and only falls back to the clone when elementSchema is actually
        // description-stripped. See its doc for the full rationale.
        const schema = pickHydrationSchema(detail.elementSchema, detail.elementSchemaJson);
        if (schema) {
            setSchemaFields(extractSchemaFields(schema));
        }

        return detail;
    }

    // ============ Create / Update Version ============
    /**
     * Create new version for an existing data element
     * POST /data-element-versions/:dataElementId/versions
     */
    async function createNewVersion(dataElementId, payload) {
        setLoading(true)
        const [err, response] = await api.createNewVersion(dataElementId, payload);
        setLoading(false)
        if (err) {
            setError(err);
            return { error: err };
        }
        return response;
    }

    /**
     * Create initial data element versions (batch)
     * POST /data-element-versions
     */
    async function createInitialVersions(payload) {
        setLoading(true)
        const [err, response] = await api.createInitial(payload);
        setLoading(false)
        if (err) {
            setError(err);
            return { error: err };
        }
        return response;
    }

    // ============ Promote Version ============
    /**
     * Promote version: DRAFT → ACTIVE → DEPRECATED → RETIRED
     * POST /data-element-versions/:dataElementId/versions/:version/promote
     */
    async function promoteVersion(dataElementId, version, targetStatus, expiryDate) {
        setLoading(true)
        const payload = { targetStatus };
        if (expiryDate) payload.expiryDate = expiryDate;
        const [err, response] = await api.promoteVersion(dataElementId, version, payload);
        setLoading(false)
        if (err) {
            setError(err);
            return { error: err };
        }
        // Sync local cache with the new status so consumers re-render with
        // ACTIVE/DEPRECATED/RETIRED before any pending autosave timer fires.
        if (response && response.status) {
            setSelectedVersion((prev) => (prev ? { ...prev, ...response } : response));
        }
        return response;
    }

    // ============ Delete Version ============
    /**
     * DELETE /data-element-versions/:dataElementId/versions/:version
     * Delete a DRAFT version.
     */
    async function deleteVersion(dataElementId, version) {
        setLoading(true)
        const [err, response] = await api.deleteVersion(dataElementId, version);
        setLoading(false)
        if (err) {
            setError(err);
            return { error: err };
        }
        return response;
    }

    // ============ Edit Version Number ============
    /**
     * PATCH /data-element-versions/:dataElementId/versions/:version/version-number
     * Edit a DRAFT version's number. Surfaces 409 clash via { conflict: true }.
     */
    async function updateVersionNumber(dataElementId, version, newVersion) {
        setLoading(true)
        const [err, response] = await api.updateVersionNumber(dataElementId, version, newVersion);
        setLoading(false)
        if (err) {
            if (err?.status === 409 || err?.response?.status === 409) {
                return { error: err, conflict: true };
            }
            setError(err);
            return { error: err };
        }
        return response;
    }

    // ============ Patch Version (Autosave) ============
    /**
     * PATCH /data-element-versions/:dataElementId/versions/:version
     * Persist draft schema changes. Handles 409 optimistic-locking conflict.
     */
    async function patchVersion(dataElementId, version, payload) {
        setConflictError(null);
        const [err, response] = await api.patchVersion(dataElementId, version, payload);
        if (err) {
            // Detect 409 conflict
            if (err?.response?.status === 409 || err?.status === 409 || (typeof err === 'string' && err.includes('409'))) {
                setConflictError('This draft has been modified by another user.');
                return { error: err, conflict: true };
            }
            setError(err);
            return { error: err };
        }
        return response;
    }

    // ============ Create Data Element ============
    /**
     * POST /data-elements
     * Create a new data element (parent record)
     */
    async function createDataElement(payload) {
        setLoading(true);
        const [err, response] = await api.createDataElement(payload);
        setLoading(false);
        if (err) {
            setError(err);
            return { error: err };
        }
        return response;
    }

    // ============ Impact Analysis ============
    /**
     * GET /data-element-versions/:dataElementId/versions/:version/impact-analysis
     */
    async function fetchImpactAnalysis(dataElementId, version) {
        setLoading(true)
        const [err, response] = await api.getImpactAnalysis(dataElementId, version);
        setLoading(false)
        if (err) {
            return setError(err);
        }
        setImpactAnalysis(response);
        return response;
    }

    // ============ Comments ============
    /**
     * GET /data-element-version-comments?dataElementId={id}&versionId={version}
     */
    async function fetchComments(dataElementId, versionId) {
        const [err, response] = await api.getComments(dataElementId, versionId);
        if (err) {
            return setError(err);
        }
        setComments(Array.isArray(response) ? response : (response.items || []));
        return response;
    }

    /**
     * POST /data-element-version-comments
     */
    async function createComment(payload) {
        const [err, response] = await api.createComment(payload);
        if (err) {
            setError(err);
            return { error: err };
        }
        return response;
    }

    // ============ Use-Case-Persona linking ============
    /**
     * POST /usecasepersonas — attach DE to use case + personas so subscriptions can flow.
     */
    async function linkUseCasePersona(payload) {
        setLoading(true);
        const [err, response] = await api.linkUseCasePersona(payload);
        setLoading(false);
        if (err) {
            setError(err);
            return { error: err };
        }
        return response;
    }

    /** GET /usecases/list — for picker dropdown */
    async function fetchUseCases() {
        const [err, response] = await api.fetchUseCases();
        if (err) {
            setError(err);
            return [];
        }
        const items = Array.isArray(response) ? response : (response?.items || []);
        return items;
    }

    /** GET /personas — for picker dropdown */
    async function fetchPersonas() {
        const [err, response] = await api.fetchPersonas();
        if (err) {
            setError(err);
            return [];
        }
        const items = Array.isArray(response) ? response : (response?.items || []);
        return items;
    }

    /**
     * GET /data-elements/persona?withRelation=true — list the DE's UCP links.
     * Returns serialized link objects (see serializeDeLink); existing callers
     * that only check links.length keep working.
     */
    async function fetchDeLinks(dataElementId) {
        const [err, response] = await api.fetchDeLinks(dataElementId);
        if (err) {
            return [];
        }
        const items = Array.isArray(response) ? response : (response?.items || []);
        const links = items.map(serializeDeLink);
        if (!dataElementId) return links;
        return links.filter((row) => row?.dataElementId === dataElementId);
    }

    /**
     * POST /data-elements/persona/remove — unlink one DataElementPersona row.
     * Body must be IRemoveDataElementPersona[]: [{ dataElementPersonaId }].
     */
    async function removeDeLink(dataElementPersonaId) {
        setLoading(true);
        const [err, response] = await api.removeDeLink([{ dataElementPersonaId }]);
        setLoading(false);
        if (err) {
            setError(err);
            return { error: err };
        }
        return response;
    }

    /** GET /master-data-tables — for the master-data table picker dropdown */
    async function fetchMasterDataTables() {
        const [err, response] = await api.fetchMasterDataTables();
        if (err) {
            setError(err);
            return [];
        }
        return Array.isArray(response) ? response : (response?.items || []);
    }

    return {
        error,
        setError,
        loading,
        setLoading,

        // List page
        items,
        allData,
        fetchList,
        fetchListWithActiveVersions,

        // Detail page
        versionHistory,
        currentVersionDetail,
        fetchAllVersions,
        fetchSingleVersion,

        // Schema editing
        schemaFields,
        setSchemaFields,
        selectedVersion,
        setSelectedVersion,

        // CRUD
        createNewVersion,
        createInitialVersions,
        promoteVersion,
        patchVersion,
        deleteVersion,
        updateVersionNumber,
        createDataElement,

        // Conflict
        conflictError,
        setConflictError,

        // Impact analysis
        impactAnalysis,
        fetchImpactAnalysis,

        // Comments
        comments,
        fetchComments,
        createComment,

        // Use-case-persona linking
        linkUseCasePersona,
        fetchUseCases,
        fetchPersonas,
        fetchDeLinks,
        removeDeLink,
        fetchMasterDataTables,
    }
}

export default useDataSchemaVersioning
